// =============================================================================
// incidentRows.js — PURE, dependency-free helpers for the Incident Report tile.
//
// Kept separate from incidentsSync.js (which pulls in the Supabase client) so
// this core logic — the storage-key builder and the submit-time validation —
// can be unit-tested in plain Node with zero setup (no browser, no network, no
// env). No PIN and no staff name ever go through here: the reporter is stamped
// server-side from the login token by the DB trigger (migration 0640).
// =============================================================================

// The closed set of severities the DB CHECK accepts (migration 0640).
export const INCIDENT_SEVERITIES = ["urgent", "normal"];

// Sanitise a path segment: lower-risk chars only, capped. Mirrors the delivery
// photo key builder (deliveryPhotoKey.js) so incident keys sit cleanly beside
// the inbound/outbound ones in the shared bucket.
function safe(s, max) {
  return String(s == null ? "" : s).replace(/[^a-z0-9]+/gi, "_").slice(0, max);
}

// Pick a file extension from the mime type (jpeg default).
export function extFor(mime) {
  const m = String(mime || "").toLowerCase();
  if (m.includes("png")) return "png";
  if (m.includes("webp")) return "webp";
  if (m.includes("heic") || m.includes("heif")) return "heic";
  if (m.includes("gif")) return "gif";
  return "jpg";
}

// Storage key for one incident photo. SITE-KEY-FIRST on purpose: the first path
// segment is a real site key, so the upload already satisfies migration 0423's
// restrictive prefix policy WITHOUT editing it (no storage-admin dashboard step,
// no silent-refusal trap). `token` is the incident's stable client token, so a
// retry of the same photo re-uses the same key and upsert:false makes it a no-op.
// No staff identity ever goes in the path — only site / day / token / index.
export function incidentPhotoKey({ siteKey, day, token, idx = 0, mime } = {}) {
  const site = safe(siteKey, 24) || "unknown";
  const d = safe(day, 12) || "nodate";
  const tok = safe(token, 40) || "notoken";
  return `${site}/incidents/${d}/${tok}__${idx}.${extFor(mime)}`;
}

// Submit-time validation. Returns the FIRST offending field + a message, or null
// if the report can be filed. Required: a type, an urgency, and a written
// account. Photo is NOT required — per JJ (3 Sep) it is strongly recommended,
// not a hard block (an urgent allergen report must not be stopped by a missing
// photo). Keep ALL validation here so the tile and the sync layer agree and it
// unit-tests in plain Node.
export function incidentError({ type, severity, description } = {}) {
  if (!type || !String(type).trim()) return { field: "type", msg: "Pick a type" };
  if (!INCIDENT_SEVERITIES.includes(severity)) return { field: "severity", msg: "Pick an urgency" };
  if (!description || !String(description).trim()) return { field: "description", msg: "Describe what happened" };
  return null;
}

// Exposed for the unit test only.
export const _safe = safe;
